import { Component, OnInit, ViewContainerRef, ɵConsole, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute, Route } from '@angular/router';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';

import { User } from '../_models/User';
import { ClientType } from '../_models/ClientType';
import { Role } from '../_models/Role';

import { UserService } from '../_services/user.service';
import { Organization } from '../_models/Organization';
import { Engine } from '../_models/Engine';
import { OrganizationService } from '../_services/organization.service';
import { AuthService } from '../_services/auth.service';
import { DialogService } from '../_services/dialog.service';
import { NgxSpinnerService } from 'ngx-spinner';

import { getUrl } from '../_shared/utils';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';

@Component({
    selector: 'app-user-create',
    templateUrl: './user-create.component.html',
    styleUrls: ['./user-create.component.css']
})

export class UserCreateComponent implements OnInit, OnDestroy {
    private _onDestroy$ = new Subject<void>();

    get hasAllUsers() {
        return this._hasAllUsers;
    }
    get hasOrgUsers() {
        return this._hasOrgUsers;
    }
    get staffUser() {
        return this._staffUser;
    }
    get selectedUser() {
        return this._selectedUser;
    }
    get editMode() {
        return this._editMode;
    }

    constructor(
        private authService: AuthService,
        private userService: UserService,
        private organizationService: OrganizationService,
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private spinner: NgxSpinnerService,
        private dialogService: DialogService,
        private viewRef: ViewContainerRef
    ) {
        // Get lightShow to edit from route
        this.route.params.pipe(takeUntil(this._onDestroy$)).subscribe(params => { this._editId = params['id']; });

        this._editMode = this._editId > 0;
    }

    // Environment Variables
    baseUrl = getUrl();

    public clientTypes: ClientType[] = [];
    public organizations: Organization[] = [];
    public engines: Engine[] = [];
    public roles: Role[] = [];

    public userEmail: string;
    public oldUserEmail: string;
    public userPhoneNumber: string;
    public shouldNotifyUserCreated = true;
    public selectedEngine: Engine;
    public selectedEngineId: number;
    public selectedClientType: ClientType;
    public selectedClientTypeId: number;
    public selectedOrganization: Organization;
    public selectedOrganizationId: number;
    public selectedRole: Role;
    public selectedRoleId: number;
    public enableSubscription = false;
    public canProgramCustomTracks = false;
    public createUserApiKey = false;
    public enableNetworkSync = false;
    public hasApiKey = false;
    public maxDailyRate = 0;

    private _hasAllUsers: boolean;

    private _hasOrgUsers: boolean;
    private _staffUser: boolean;
    public hasSiteMangement: boolean;

    private _selectedUser: User;

    private _editMode = false;

    public loading = false;

    public userForm: FormGroup;

    private _editId = -1;

    public userSubmitted = false;

    ngOnInit() {
        this.spinner.show();
        this.loading = true;

        this.userService.hasSinglePermission('ViewAllUsers').pipe(map(
            data => { this._hasAllUsers = data; })).subscribe();

        this.userService.hasSinglePermission('ViewOrganizationUsers').pipe(map(
            data => { this._hasOrgUsers = data; })).subscribe();

        this.userService.hasSinglePermission('ReportActivity').pipe(map(
            data => { this._staffUser = data; })).subscribe();

        this.userService.hasSinglePermission('SiteManagement').pipe(map(
            data => { this.hasSiteMangement = data; })).subscribe();

            

        // Set up validation
        this.userForm = this.fb.group({
            shouldNotifyUserCreatedInput: [false],
            enableSubscriptionPeriodInput: [false],
            canProgramCustomTracks: [false],
            enableNetworkSync: [false],
            canEditVenues: [false],
            emailInput: ['', Validators.required],
            phoneNumberInput: [''],
            engineIdSelect: [this.selectedEngine, Validators.required],
            clientTypeIdSelect: [this.selectedClientType, Validators.required],
            organizationIdSelect: [this.selectedOrganization],
            roleIdSelect: [this.selectedRole],
            period: { value: { startDate: new Date(), endDate: new Date(), isNew: false }, disabled: true },
            createUserApiKey: [false],
            maxDailyRate: ['']
        });

        const organizationObs = this.organizationService.getAllOrganizations();
        const engineObs = this.userService.getUserEngines();
        const rolesObs = this.userService.getRoles();

        forkJoin([organizationObs, engineObs, rolesObs]).subscribe(results => {
            this.organizations = results[0];
            this.engines = results[1];
            this.roles = results[2];

            if (this.organizations != null) {
                this.selectedOrganization = this.organizations[0];
                this.selectedOrganizationId = this.organizations[0].organizationId;
                this.userService.getClientTypes(this.selectedOrganization.organizationId).subscribe(data => {
                    this.clientTypes = data;
                    this.loading = false;
                    if (!this._editMode) {
                        this.selectedClientType = this.clientTypes[0];
                        this.selectedClientTypeId = this.clientTypes[0].clientTypeId;

                        if (this.engines != null) {
                            this.selectedEngine = this.engines.find(e => e.engineId === 6);
                            this.selectedEngineId = 6; // this.engines[0].engineId;
                        }

                        if (this.roles != null) {
                            this.selectedRole = this.roles[1];
                            this.selectedRoleId = this.roles[1].roleId;
                        }
                    } else {
                        this.getUser(this._editId);
                    }
                });
            }

                this.spinner.hide();
            });
    }

    canDeactivate(): Promise<boolean> | boolean {
        if (!this.userForm.dirty || this.userSubmitted) { return true; }
        // Return a Promise<boolean>
        return this.dialogService.confirm(
            'Unsaved Changes',
            'Are you sure you want to continue? Any unsaved changes will be lost!',
            this.viewRef
        );
    }

    public onSelectOrganization() {
        this.loading = true;
        this.userService
            .getClientTypes(this.selectedOrganization.organizationId)
            .pipe(takeUntil(this._onDestroy$))
            .subscribe(data => {
                this.clientTypes = data;
                if (!this._editMode && this.clientTypes.length > 0) {
                    this.selectedClientType = this.clientTypes[0];
                    this.selectedClientTypeId = this.clientTypes[0].clientTypeId;
                }
                this.loading = false;
            });
    }

    // Fill form data from existing lightShow
    public getUser(userId: number) {
        this.userService.getUser(userId)
            .pipe(takeUntil(this._onDestroy$))
            .subscribe(user => {
                this._selectedUser = user;
                if (this._selectedUser != null) {
                    this.userEmail = this._selectedUser.email;
                    this.oldUserEmail = this._selectedUser.email;
                    this.userPhoneNumber = this._selectedUser.phoneNumber;
                    this.selectedEngine = this._selectedUser.engine;
                    this.selectedClientType = this._selectedUser.clientType;
                    this.selectedEngineId = this._selectedUser.engine.engineId;
                    this.selectedClientTypeId = this._selectedUser.clientType.clientTypeId;
                    this.canProgramCustomTracks = this._selectedUser.canProgramCustomTracks;
                    this.enableNetworkSync = this._selectedUser.enableNetworkSync;
                    this.hasApiKey = this._selectedUser.hasApiKey;
                    this.maxDailyRate = this._selectedUser.maxDailyRate;
                    this.userForm.controls.canEditVenues.patchValue(this._selectedUser.canEditVenues);

                    if (this._selectedUser.subscriptionStartDate && this._selectedUser.subscriptionEndDate) {
                        this.userForm.controls.period.enable();
                        this.enableSubscription = true;
                        this.userForm.controls.period.patchValue({

                            startDate: new Date(this._selectedUser.subscriptionStartDate),
                            endDate: new Date(this._selectedUser.subscriptionEndDate)
                        });
                    }

                    if (
                        this.selectedOrganizationId !==
                        this._selectedUser.organization.organizationId
                    ) {
                        this.selectedOrganization = this._selectedUser.organization;
                        this.selectedOrganizationId = this._selectedUser.organization.organizationId;
                    }

                    if (
                        this.selectedRoleId !==
                        this._selectedUser.role.roleId
                    ) {
                        this.selectedRole = this._selectedUser.role;
                        this.selectedRoleId = this._selectedUser.role.roleId;
                    }
                }
            }, error => {
                alert(error.message);
            });
    }

    public submitUser() {
        this.userSubmitted = true;
        const formData = Object.assign({}, this.userForm.value);
        const newUser: any = {
            PhoneNumber: this.userPhoneNumber,
            EngineId: this.selectedEngine.engineId,
            SubscriptionStartDate: this.enableSubscription ? this.formatDate(formData.period.startDate) : null,
            SubscriptionEndDate: this.enableSubscription ? this.formatDate(formData.period.endDate) : null,
            OrganizationId: this.selectedOrganization ? this.selectedOrganization.organizationId : -1,
            ClientTypeId: this.selectedClientType.clientTypeId,
            RoleId: this.selectedRole ? this.selectedRole.roleId : -1,
            CanProgramCustomTracks: this.canProgramCustomTracks,
            EnableNetworkSync: this.enableNetworkSync,
            CreateUserApiKey: this.createUserApiKey,
            MaxDailyRate: this.maxDailyRate,
            CanEditVenues: this.userForm.controls.canEditVenues.value,
        };

        if (!this.editMode) {
            const newPassword = [];
            const possibleChars = 'ABCDEFGHJKLMNOPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz0123456789!@#$%^&*_+=';
            const possibleNumbers = '0123456789';
            const possibleSymbols = '!@#$%^&*_+=';

            // add letters
            for (let i = 0; i < 8; i++) {
                newPassword.push(possibleChars.charAt(Math.floor(Math.random() * possibleChars.length)));
            }

            // add a number
            newPassword.push(possibleNumbers.charAt(Math.floor(Math.random() * possibleNumbers.length)));

            // add a symbol
            newPassword.push(possibleSymbols.charAt(Math.floor(Math.random() * possibleSymbols.length)));

            let currentIndex = newPassword.length, temporaryValue, randomIndex;

            // shuffle the password
            while (0 !== currentIndex) {
                randomIndex = Math.floor(Math.random() * currentIndex);
                currentIndex -= 1;

                temporaryValue = newPassword[currentIndex];
                newPassword[currentIndex] = newPassword[randomIndex];
                newPassword[randomIndex] = temporaryValue;
            }

            newUser['Notify'] = this.shouldNotifyUserCreated;
            newUser['Email'] = this.userEmail;
            newUser['Password'] = newPassword.join('');
            this.createUser(newUser);
        } else {
            newUser['OldEmail'] = this.oldUserEmail;
            newUser['NewEmail'] = this.userEmail;
            this.updateUser(newUser);
        }
    }

    private formatDate(date: Date): string {
        return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
    }

    private createUser(newUser: User) {
        this.loading = true;
        this.userService.createUser(newUser).pipe(takeUntil(this._onDestroy$)).subscribe(_ => {
            this.dialogService.alert(
                'Success',
                'New user registered successfully!',
                this.viewRef).then(() => {
                    this.router.navigate(['/portal/users']);
                });
        }, error => {
            if (error.error.Email) {
                this.dialogService.alert(
                    'Failed to Create New User',
                    'A user with that email address already exists. Please choose a different address and try again.',
                    this.viewRef);
            }
            this.loading = false;
            console.log('Failed to register');
        }, () => {
            this.loading = false;
        });
    }

    private updateUser(user: User) {
        this.loading = true;
        this.userService
            .updateUser(user)
            .pipe(takeUntil(this._onDestroy$))
            .subscribe(_ => {
                this.dialogService.alert(
                    'Success',
                    'User updated successfully!',
                    this.viewRef).then(() => {
                        this.router.navigate(['/portal/users']);
                    });
            }, error => {
                console.log('Failed to update');
            }, () => {
                this.loading = false;
            });
    }

    public toggleSubscription() {
        if (this.enableSubscription) {
            this.userForm.controls.period.enable();
        } else {
            this.userForm.controls.period.disable();
        }
    }

    public cancel() {
        this.router.navigate(['/portal/users']);
    }

    ngOnDestroy() {
        this._onDestroy$.next();
        this._onDestroy$.complete();
    }
}
